import { reviewDateMinute, thousands, zoneOffset } from '../configStudio/configModel';
import { TC } from '../configStudio/kit/ConfigPanel';
import { Translate } from '../wizard/adapters/ruleAdapter';
import { IReviewRow } from '../wizard/kit/ReviewTable';
import { RewardStep, RewardWizardState } from './rewardModel';

const TR = `${TC}.reward`;
const RV = `${TC}.reward.review`;

const title = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

const redemptionValue = (state: RewardWizardState, t: Translate) => {
  switch (state.type) {
    case 'url':
      return state.urlType === 'unique'
        ? t(`${RV}.uniqueUrl`)
        : t(`${RV}.staticUrl`, { value: state.url.trim() });
    case 'api':
      return t(`${RV}.apiSource`, { value: state.api?.name ?? '' });
    case 'pin':
      return t(`${RV}.pinSource`, { value: state.merchant?.name ?? '' });
    default:
      switch (state.source) {
        case 'fixed':
          return t(`${RV}.fixedSource`, { value: state.codeValue.trim() });
        case 'generate':
          return t(`${RV}.generateSource`);
        case 'csv':
          return t(`${RV}.csvSource`);
        default:
          return t(`${RV}.inventorySource`, { value: state.inventory?.name ?? '' });
      }
  }
};

const limitsValue = (state: RewardWizardState, t: Translate) => {
  const { limits } = state;
  const period = (value: string) => t(`${TC}.period.${value}`);
  const first: string[] = [];
  const second: string[] = [];
  if (limits.issuanceOn) {
    first.push(
      t(`${RV}.issuance`, {
        value: thousands(limits.issuance),
        period: period(limits.issuancePeriod)
      })
    );
  }
  if (limits.lifetimeOn) first.push(t(`${RV}.lifetime`, { value: thousands(limits.lifetime) }));
  if (limits.perUserOn) {
    second.push(
      t(`${RV}.perUser`, { value: thousands(limits.perUser), period: period(limits.perUserPeriod) })
    );
  }
  if (limits.redemptionOn) {
    second.push(
      t(`${RV}.redemptionCap`, {
        value: thousands(limits.redemption),
        period: period(limits.redemptionPeriod)
      })
    );
  }
  const lines = [first, second].filter(line => line.length > 0).map(line => line.join('  ·  '));
  return lines.length > 0 ? lines.join('\n') : t(`${TC}.noLimit`);
};

/** The rows of 66, each linking back to the step that answered it. */
export const rewardReviewRows = (
  state: RewardWizardState,
  t: Translate,
  timezone: string,
  now: string
): IReviewRow<RewardStep>[] => {
  const dated = (value: string) =>
    `${reviewDateMinute(value, timezone)} (UTC${zoneOffset(timezone, value)})`;
  const availability = [
    t(`${RV}.start`, { value: dated(state.beginsAt ?? now) }),
    state.endsOn && state.endsAt !== null
      ? t(`${RV}.end`, { value: dated(state.endsAt) })
      : t(`${RV}.noEnd`)
  ];
  if (state.window.on) {
    availability.push(
      t(`${RV}.days`, {
        value: `${state.window.days.map(title).join(', ')}  |  ${state.window.from} – ${
          state.window.to
        }`
      })
    );
  }
  const expiry = {
    never: t(`${RV}.neverExpires`),
    fixed: t(`${RV}.fixedExpiry`, {
      value: state.expiresAt === null ? '' : dated(state.expiresAt)
    }),
    days: t(`${RV}.daysExpiry`, { value: state.expiryDays })
  }[state.expiry];
  const selling =
    state.sellOn && state.sellLoyalty !== null
      ? t(`${RV}.sellingValue`, {
          points: thousands(state.sellPoints),
          name: state.sellLoyalty.name,
          tiers: state.sellTiers.length > 0 ? `; ${state.sellTiers.join(', ')}` : ''
        })
      : t(`${RV}.notForSale`);
  return [
    {
      key: 'type',
      label: t(`${RV}.type`),
      value: t(`${TR}.typeName.${state.type ?? 'text'}`),
      edit: 'type'
    },
    {
      key: 'availability',
      label: t(`${RV}.availability`),
      value: availability.join('\n'),
      edit: 'visibility'
    },
    {
      key: 'redemption',
      label: t(`${RV}.redemption`),
      value: redemptionValue(state, t),
      edit: 'source'
    },
    { key: 'expiry', label: t(`${RV}.expiry`), value: expiry, edit: 'expiry' },
    {
      key: 'cost',
      label: t(`${RV}.cost`),
      value: state.cost === '' ? t(`${TC}.none`) : `${state.costCurrency} ${state.cost}`,
      edit: 'limits'
    },
    { key: 'selling', label: t(`${RV}.selling`), value: selling, edit: 'limits' },
    {
      key: 'visibility',
      label: t(`${RV}.visibility`),
      value: t(`${RV}.${state.visibility}Value`),
      edit: 'visibility'
    },
    { key: 'limits', label: t(`${RV}.limits`), value: limitsValue(state, t), edit: 'limits' }
  ];
};
